import React, { useState } from "react";
import { useSyncStore } from "@/store/sync.store";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import { Button } from "@/components/button";
import { Badge } from "@/components/badge";
import { Card } from "@/components/card";
import { cn } from "@/utilities";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CloudUpload,
  Loader2,
  Minimize2,
  XCircle,
  AlertCircle,
} from "lucide-react";

/**
 * SyncMonitor - Modal de Monitoreo de Sincronización
 *
 * Muestra el progreso de sincronización masiva con:
 * - Barra de progreso visual
 * - Contadores de éxito/error
 * - Lista desplegable de resultados detallados
 * - Opción de minimizar a burbuja flotante
 */
export const SyncMonitor: React.FC = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const isVisible = useSyncStore((state) => state.isVisible);
  const isMinimized = useSyncStore((state) => state.isMinimized);
  const totalCount = useSyncStore((state) => state.totalCount);
  const successCount = useSyncStore((state) => state.successCount);
  const errorCount = useSyncStore((state) => state.errorCount);
  const results = useSyncStore((state) => state.results);
  const statusMessage = useSyncStore((state) => state.statusMessage);

  const minimize = useSyncStore((state) => state.minimize);
  const close = useSyncStore((state) => state.close);
  const getProgress = useSyncStore((state) => state.getProgress);
  const isComplete = useSyncStore((state) => state.isComplete);

  const progress = getProgress();
  const complete = isComplete();

  // No mostrar si está minimizado o no visible
  if (!isVisible || isMinimized) return null;

  /**
   * Formatea el monto para mostrar
   */
  const formatAmount = (amount?: number): string => {
    if (amount === undefined) return "";
    return `S/ ${amount.toFixed(2)}`;
  };

  /**
   * Obtiene el icono según el estado del resultado
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "error":
        return <XCircle className="h-4 w-4 text-destructive" />;
      case "syncing":
        return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  /**
   * Obtiene el label del tipo de registro
   */
  const getTypeLabel = (type: string) => {
    switch (type) {
      case "sale":
        return "Venta";
      case "cancellation":
        return "Anulación";
      case "refund":
        return "Devolución";
      default:
        return "Registro";
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent
        className="sm:max-w-[500px]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2">
            <CloudUpload className="h-5 w-5 text-primary" />
            Sincronización en Progreso
          </DialogTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={minimize}
              title="Minimizar"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Barra de Progreso */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{statusMessage}</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <div className="h-3 w-full bg-secondary rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full transition-all duration-300 ease-out rounded-full",
                  complete
                    ? errorCount > 0
                      ? "bg-yellow-500"
                      : "bg-green-500"
                    : "bg-primary"
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Contadores */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-primary">{totalCount}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </Card>
            <Card className="p-3 text-center border-green-200">
              <p className="text-2xl font-bold text-green-500">{successCount}</p>
              <p className="text-xs text-muted-foreground">Exitosos</p>
            </Card>
            <Card className="p-3 text-center border-red-200">
              <p className="text-2xl font-bold text-destructive">{errorCount}</p>
              <p className="text-xs text-muted-foreground">Con Error</p>
            </Card>
          </div>

          {/* Sección Desplegable de Detalles */}
          <div className="border rounded-md">
            <button
              onClick={() => setIsDetailsOpen(!isDetailsOpen)}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <span className="text-sm font-medium">
                Ver detalle de registros ({results.length})
              </span>
              {isDetailsOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            {isDetailsOpen && (
              <div className="border-t max-h-[200px] overflow-y-auto">
                {results.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">
                    Esperando resultados...
                  </p>
                ) : (
                  <div className="divide-y">
                    {results.map((result) => (
                      <div
                        key={result.id}
                        className={cn(
                          "p-3 flex items-start gap-3",
                          result.status === "error" && "bg-destructive/5"
                        )}
                      >
                        {getStatusIcon(result.status)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {getTypeLabel(result.type)}
                            </Badge>
                            {result.displayData?.documentType && (
                              <span className="text-xs text-muted-foreground">
                                {result.displayData.documentType}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {result.displayData?.amount && (
                              <span className="text-sm font-medium">
                                {formatAmount(result.displayData.amount)}
                              </span>
                            )}
                            {result.displayData?.customerName && (
                              <span className="text-xs text-muted-foreground truncate">
                                {result.displayData.customerName}
                              </span>
                            )}
                          </div>
                          {result.status === "success" && (
                            <p className="text-xs text-green-600 mt-1">
                              Sincronización exitosa
                            </p>
                          )}
                          {result.errorMessage && (
                            <p className="text-xs text-destructive mt-1 break-all">
                              {result.errorCode && `[${result.errorCode}] `}
                              {result.errorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Botones de Acción */}
          <div className="flex items-center justify-end gap-2 pt-2">
            {complete ? (
              <Button onClick={close}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Cerrar
              </Button>
            ) : (
              <Button variant="outline" onClick={minimize}>
                <Minimize2 className="h-4 w-4 mr-2" />
                Minimizar
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SyncMonitor;
